import React, { useState, useEffect } from "react";
import { useAuth } from "../context/AuthContext";
import { useNavigate, Link } from "react-router-dom";
import {
  createUserWithEmailAndPassword,
  updateProfile
} from "firebase/auth";
import {
  doc,
  setDoc,
  serverTimestamp
} from "firebase/firestore";
import { auth, db } from "../firebase";

const ALLOW_SELF_SIGNUP = (process.env.REACT_APP_ALLOW_SELF_SIGNUP ?? "true") === "true";

export default function Signup() {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [email, setEmail] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (user) navigate("/dashboard", { replace: true });
  }, [user, navigate]);

  if (!ALLOW_SELF_SIGNUP) {
    return (
      <div className="min-h-[70vh] grid place-items-center px-4">
        <div className="rounded-2xl border border-[#D4FF00]/30 bg-black/70 p-6 text-center">
          <div className="text-2xl font-bold text-[#D4FF00] mb-2">Sign up closed</div>
          <p className="text-gray-300">Please contact staff to create an account.</p>
          <Link className="underline text-gray-200 mt-3 inline-block" to="/login">Back to login</Link>
        </div>
      </div>
    );
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErr("");
    setLoading(true);

    try {
      const cred = await createUserWithEmailAndPassword(auth, email.trim(), password);

      if (displayName) {
        await updateProfile(cred.user, { displayName });
      }

      // ✅ Explicitly create Firestore user doc
      await setDoc(doc(db, "users", cred.user.uid), {
        displayName: displayName || cred.user.displayName || cred.user.email,
        email: cred.user.email,
        credits: 0,
        createdAt: serverTimestamp()
      }, { merge: true });

      navigate("/dashboard", { replace: true });

    } catch (e) {
      const code = e.code || "";
      if (code.includes("email-already-in-use")) setErr("Email already in use.");
      else if (code.includes("weak-password")) setErr("Password is too weak.");
      else setErr("Sign up failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-[70vh] flex items-center justify-center px-4">
      <div className="w-full max-w-md rounded-2xl border border-[#D4FF00]/30 bg-black/70 backdrop-blur p-6 shadow-[0_0_40px_rgba(212,255,0,0.15)]">
        <div className="mb-6 text-center">
          <h1 className="text-2xl font-extrabold tracking-wide text-[#D4FF00]">
            Create Account
          </h1>
          <p className="text-sm text-gray-300 mt-1">Revelstoke Gaming Café</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm mb-1 text-gray-200">Display name (optional)</label>
            <input
              className="w-full rounded-lg border border-[#D4FF00]/30 bg-neutral-900 text-gray-100 p-2.5 focus:ring-2 focus:ring-[#D4FF00] focus:border-transparent"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              placeholder="GamerTag"
            />
          </div>

          <div>
            <label className="block text-sm mb-1 text-gray-200">Email</label>
            <input
              className="w-full rounded-lg border border-[#D4FF00]/30 bg-neutral-900 text-gray-100 p-2.5 focus:ring-2 focus:ring-[#D4FF00] focus:border-transparent placeholder:text-gray-500"
              type="email"
              autoComplete="email"
              placeholder="you@example.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
          </div>

          <div>
            <label className="block text-sm mb-1 text-gray-200">Password</label>
            <input
              className="w-full rounded-lg border border-[#D4FF00]/30 bg-neutral-900 text-gray-100 p-2.5 focus:ring-2 focus:ring-[#D4FF00] focus:border-transparent"
              type="password"
              autoComplete="new-password"
              placeholder="••••••••"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />
          </div>

          {err && <div className="text-[#ff6b6b] text-sm">{err}</div>}

          <button
            disabled={loading}
            className="w-full py-2.5 rounded-lg bg-[#D4FF00] text-black font-semibold hover:brightness-90 transition disabled:opacity-60"
            type="submit"
          >
            {loading ? "Creating…" : "Create account"}
          </button>
        </form>

        <div className="mt-3 text-sm text-gray-300">
          Already have an account?{" "}
          <Link className="underline hover:text-white" to="/login">Sign in</Link>
        </div>
      </div>
    </div>
  );
}
